import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheckIcon,
  ShieldExclamationIcon,
  MagnifyingGlassIcon,
  AcademicCapIcon,
  CalendarDaysIcon,
  BuildingOffice2Icon,
  CheckBadgeIcon,
  LinkIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import api from '../utils/api';

const COLORS = {
  navy: '#0D1B2A',
  teal: '#00B4D8',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  grey: '#6B7280',
};

const CEFR_COLORS = {
  A1: { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' },
  A2: { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' },
  B1: { bg: '#DBEAFE', text: '#1E40AF', border: '#BFDBFE' },
  B2: { bg: '#DBEAFE', text: '#1E40AF', border: '#BFDBFE' },
  C1: { bg: '#F0FDF4', text: '#065F46', border: '#BBF7D0' },
  C2: { bg: '#F0FDF4', text: '#065F46', border: '#BBF7D0' },
};

function CefrBadge({ level }) {
  if (!level) return null;
  const c = CEFR_COLORS[level] || { bg: '#F1F5F9', text: '#475569', border: '#CBD5E1' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 14px', borderRadius: 20,
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
      fontSize: 15, fontWeight: 800, letterSpacing: '0.05em',
    }}>
      {level}
    </span>
  );
}

function DetailRow({ icon: Icon, label, value, accent }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      padding: '12px 0', borderBottom: '1px solid #F1F5F9',
      gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {Icon && <Icon style={{ width: 17, height: 17, color: COLORS.grey, flexShrink: 0 }} />}
        <span style={{ fontSize: 13, color: COLORS.grey, fontWeight: 500 }}>{label}</span>
      </div>
      <span style={{ fontSize: 14, fontWeight: 700, color: accent || COLORS.navy, textAlign: 'right' }}>
        {value}
      </span>
    </div>
  );
}

const Spinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ repeat: Infinity, duration: 0.85, ease: 'linear' }}
      style={{
        width: 44, height: 44, borderRadius: '50%',
        border: `4px solid ${COLORS.teal}22`, borderTopColor: COLORS.teal,
      }}
    />
  </div>
);

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function maskName(fullName) {
  if (!fullName) return '—';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0] + '.' ;
  const first = parts[0][0] + '.';
  const last = parts[parts.length - 1];
  return `${first} ${last}`;
}

export default function VerifyIdPage() {
  const { idNumber: urlIdNumber } = useParams();
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState(urlIdNumber || '');
  const [queriedId, setQueriedId] = useState(urlIdNumber || '');
  const [result, setResult] = useState(null); // null = not searched, {} = searched
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pin, setPin] = useState('');
  const [pinResult, setPinResult] = useState(null);
  const [pinLoading, setPinLoading] = useState(false);
  const [pinError, setPinError] = useState('');
  const pinRefs = [useRef(), useRef(), useRef(), useRef()];

  const handlePinDigit = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const digits = pin.split('');
    digits[i] = val;
    const newPin = digits.join('').slice(0, 4);
    setPin(newPin);
    setPinError('');
    if (val && i < 3) pinRefs[i + 1].current?.focus();
    if (newPin.length === 4) submitPin(newPin);
  };

  const handlePinKey = (i, e) => {
    if (e.key === 'Backspace' && !pin[i] && i > 0) {
      pinRefs[i - 1].current?.focus();
      const digits = pin.split('');
      digits[i - 1] = '';
      setPin(digits.join(''));
    }
  };

  const submitPin = async (pinValue) => {
    setPinLoading(true); setPinError(''); setPinResult(null);
    try {
      const r = await api.post('/api/checkin', { tutor_id_number: queriedId, pin: pinValue || pin });
      setPinResult(r.data);
      setPin('');
    } catch (e) {
      const msg = e.response?.data?.error || 'Failed';
      if (msg.toLowerCase().includes('pin')) {
        setPinError('Wrong PIN — try again');
        setPin('');
        setTimeout(() => pinRefs[0].current?.focus(), 50);
      } else {
        setPinResult({ error: true, message: msg });
      }
    }
    setPinLoading(false);
  };

  const lookupId = useCallback((idToLookup) => {
    if (!idToLookup || !idToLookup.trim()) return;
    const trimmed = idToLookup.trim().toUpperCase();
    setLoading(true);
    setError('');
    setResult(null);
    api.get(`/api/verify-id/${encodeURIComponent(trimmed)}`)
      .then(res => {
        setResult({ valid: true, data: res.data });
      })
      .catch(err => {
        if (err?.response?.status === 404) {
          setResult({ valid: false });
        } else {
          setError('Unable to reach the verification service. Please try again.');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // Auto-lookup on mount if URL param provided
  useEffect(() => {
    if (urlIdNumber) {
      lookupId(urlIdNumber);
    }
  }, [urlIdNumber, lookupId]);

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = searchInput.trim();
    if (!trimmed) return;
    setQueriedId(trimmed);
    // Update URL without hard reload
    navigate(`/verify-id/${encodeURIComponent(trimmed)}`, { replace: true });
    lookupId(trimmed);
  };

  const pageUrl = queriedId
    ? `${window.location.origin}/verify-id/${queriedId}`
    : window.location.href;

  return (
    <div style={{ minHeight: '100vh', background: '#F0F4F8', fontFamily: "'Inter', sans-serif", display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: COLORS.navy, padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8, background: COLORS.teal,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <AcademicCapIcon style={{ width: 22, height: 22, color: '#fff' }} />
        </div>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 20, letterSpacing: '-0.5px' }}>SpeakIQ</span>
        <span style={{
          marginLeft: 10, background: 'rgba(0,180,216,0.2)', color: COLORS.teal,
          borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 600,
        }}>
          ID Verification Portal
        </span>
      </div>

      {/* Main */}
      <div style={{ flex: 1, maxWidth: 560, margin: '0 auto', padding: '40px 24px', width: '100%' }}>
        {/* Page title */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <ShieldCheckIcon style={{ width: 52, height: 52, color: COLORS.teal, margin: '0 auto 12px' }} />
            <h1 style={{ margin: '0 0 8px', fontSize: 26, fontWeight: 800, color: COLORS.navy }}>
              Student ID Verification
            </h1>
            <p style={{ margin: 0, fontSize: 15, color: COLORS.grey }}>
              Verify the authenticity of a SpeakIQ school ID card
            </p>
          </motion.div>
        </div>

        {/* Search box */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          style={{
            background: '#fff', borderRadius: 14, padding: 24,
            boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
            marginBottom: 28,
          }}
        >
          <label style={{
            display: 'block', fontSize: 13, fontWeight: 700, color: COLORS.navy,
            marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            Enter School ID to Verify
          </label>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value.toUpperCase())}
                placeholder="e.g. SIQ-2024-00123"
                style={{
                  width: '100%', padding: '11px 14px 11px 40px',
                  borderRadius: 9, border: '1.5px solid #CBD5E1',
                  fontSize: 15, color: COLORS.navy, outline: 'none',
                  fontFamily: "'Courier New', monospace", fontWeight: 600,
                  letterSpacing: '0.05em', boxSizing: 'border-box',
                }}
              />
              <MagnifyingGlassIcon style={{
                width: 18, height: 18, color: COLORS.grey,
                position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                pointerEvents: 'none',
              }} />
            </div>
            <button
              type="submit"
              disabled={loading || !searchInput.trim()}
              style={{
                padding: '11px 20px', borderRadius: 9,
                background: (loading || !searchInput.trim()) ? '#94A3B8' : COLORS.teal,
                color: '#fff', border: 'none', fontWeight: 700, fontSize: 14,
                cursor: (loading || !searchInput.trim()) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
                transition: 'background 0.2s', whiteSpace: 'nowrap',
              }}
            >
              {loading ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}
                  style={{ width: 16, height: 16, borderRadius: '50%', border: '2.5px solid rgba(255,255,255,0.3)', borderTopColor: '#fff' }}
                />
              ) : (
                <MagnifyingGlassIcon style={{ width: 16, height: 16 }} />
              )}
              Verify
            </button>
          </form>

          {error && (
            <div style={{
              marginTop: 12, background: '#FEF2F2', border: '1px solid #FCA5A5',
              borderRadius: 8, padding: '8px 12px', color: COLORS.error, fontSize: 13,
            }}>
              {error}
            </div>
          )}
        </motion.div>

        {/* Result area */}
        <AnimatePresence mode="wait">
          {loading && (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Spinner />
            </motion.div>
          )}

          {!loading && result?.valid === true && (
            <motion.div
              key="valid"
              initial={{ opacity: 0, y: 20, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35, type: 'spring', stiffness: 200, damping: 20 }}
            >
              <div style={{
                background: '#fff', borderRadius: 16, overflow: 'hidden',
                boxShadow: '0 4px 24px rgba(16,185,129,0.15)',
                border: `2px solid ${COLORS.success}`,
              }}>
                {/* Green header band */}
                <div style={{
                  background: `linear-gradient(135deg, ${COLORS.success}, #059669)`,
                  padding: '24px 28px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 16,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 18, delay: 0.1 }}
                    >
                      <ShieldCheckIcon style={{ width: 52, height: 52, color: '#fff' }} />
                    </motion.div>
                    <div>
                      <motion.p
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        style={{ margin: '0 0 4px', fontSize: 12, color: 'rgba(255,255,255,0.8)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}
                      >
                        School ID Verified
                      </motion.p>
                      <motion.h2
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.25 }}
                        style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#fff' }}
                      >
                        {result.data?.id_number || queriedId}
                      </motion.h2>
                    </div>
                  </div>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.7 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, type: 'spring', stiffness: 260, damping: 18 }}
                    style={{
                      background: '#fff', borderRadius: 10,
                      padding: '6px 16px',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <CheckBadgeIcon style={{ width: 18, height: 18, color: COLORS.success }} />
                    <span style={{ fontSize: 15, fontWeight: 800, color: COLORS.success }}>VALID</span>
                  </motion.div>
                </div>

                {/* Details */}
                <div style={{ padding: '20px 28px' }}>
                  <DetailRow
                    icon={null}
                    label="Student"
                    value={maskName(result.data?.full_name)}
                  />
                  <DetailRow
                    icon={BuildingOffice2Icon}
                    label="School"
                    value={result.data?.school_name}
                  />
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 0', borderBottom: '1px solid #F1F5F9',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <AcademicCapIcon style={{ width: 17, height: 17, color: COLORS.grey }} />
                      <span style={{ fontSize: 13, color: COLORS.grey, fontWeight: 500 }}>CEFR Level</span>
                    </div>
                    <CefrBadge level={result.data?.cefr_level} />
                  </div>
                  <DetailRow
                    icon={CalendarDaysIcon}
                    label="Issued"
                    value={formatDate(result.data?.issued_at)}
                  />
                  <DetailRow
                    icon={CalendarDaysIcon}
                    label="Valid Until"
                    value={formatDate(result.data?.valid_until)}
                    accent={COLORS.success}
                  />

                  {/* Active status message */}
                  <div style={{
                    marginTop: 18,
                    background: '#F0FDF4', border: '1px solid #BBF7D0',
                    borderRadius: 10, padding: '12px 16px',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <CheckBadgeIcon style={{ width: 18, height: 18, color: COLORS.success, flexShrink: 0 }} />
                    <p style={{ margin: 0, fontSize: 14, color: '#065F46', fontWeight: 500 }}>
                      This student is actively enrolled at{' '}
                      <strong>{result.data?.school_name}</strong>.
                    </p>
                  </div>

                  {/* Staff PIN check-in */}
                  <div style={{ marginTop: 18, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 12, padding: 16 }}>
                    <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 700, color: COLORS.grey, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      📋 Staff — Log Attendance
                    </p>
                    {pinResult ? (
                      <div style={{ borderRadius: 10, padding: '12px 14px', textAlign: 'center',
                        background: pinResult.error ? '#FEF2F2' : pinResult.action === 'checkin' ? '#F0FDF4' : '#EFF6FF',
                        border: `1px solid ${pinResult.error ? '#FECACA' : pinResult.action === 'checkin' ? '#BBF7D0' : '#BFDBFE'}` }}>
                        <div style={{ fontSize: 22, marginBottom: 4 }}>{pinResult.error ? '❌' : pinResult.action === 'checkin' ? '✅' : '👋'}</div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.navy }}>{pinResult.message}</div>
                        {pinResult.action === 'checkout' && pinResult.duration_min != null && (
                          <div style={{ fontSize: 12, color: '#1D4ED8', marginTop: 4 }}>⏱ {Math.floor(pinResult.duration_min/60)}h {pinResult.duration_min%60}m on site</div>
                        )}
                        <button onClick={() => { setPinResult(null); setTimeout(() => pinRefs[0].current?.focus(), 50); }}
                          style={{ marginTop: 10, padding: '4px 14px', background: 'transparent', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: 12, color: COLORS.grey, cursor: 'pointer' }}>
                          Log another
                        </button>
                      </div>
                    ) : (
                      <>
                        <p style={{ margin: '0 0 10px', fontSize: 12, color: COLORS.grey, textAlign: 'center' }}>Enter school PIN to check in / out</p>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 6 }}>
                          {[0,1,2,3].map(i => (
                            <input key={i} ref={pinRefs[i]} type="tel" inputMode="numeric" maxLength={1}
                              value={pin[i] || ''}
                              onChange={e => handlePinDigit(i, e.target.value)}
                              onKeyDown={e => handlePinKey(i, e)}
                              style={{ width: 48, height: 52, textAlign: 'center', fontSize: 22, fontWeight: 800,
                                border: `2px solid ${pinError ? COLORS.error : pin[i] ? COLORS.teal : '#D1D5DB'}`,
                                borderRadius: 10, outline: 'none', background: '#fff', color: COLORS.navy }} />
                          ))}
                        </div>
                        {pinError && <p style={{ textAlign: 'center', fontSize: 13, color: COLORS.error, fontWeight: 600, margin: '4px 0 0' }}>{pinError}</p>}
                        {pinLoading && <p style={{ textAlign: 'center', fontSize: 12, color: COLORS.grey, margin: '4px 0 0' }}>Logging…</p>}
                        {!pinLoading && !pinError && <p style={{ textAlign: 'center', fontSize: 11, color: COLORS.grey, margin: '4px 0 0' }}>Auto-submits after 4 digits</p>}
                      </>
                    )}
                  </div>

                  {/* QR / URL placeholder */}
                  <div style={{
                    marginTop: 18, background: '#F8FAFC', border: '1px solid #E2E8F0',
                    borderRadius: 10, padding: '14px 16px',
                  }}>
                    <p style={{
                      margin: '0 0 8px', fontSize: 11, fontWeight: 700, color: COLORS.grey,
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                      display: 'flex', alignItems: 'center', gap: 5,
                    }}>
                      <LinkIcon style={{ width: 13, height: 13 }} /> Verification URL
                    </p>
                    {/* QR code placeholder */}
                    <div style={{
                      width: 90, height: 90, background: '#E2E8F0', borderRadius: 8,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginBottom: 10,
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        {/* Simulated QR grid */}
                        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                          <rect x="2" y="2" width="22" height="22" rx="3" fill={COLORS.navy} />
                          <rect x="8" y="8" width="10" height="10" rx="1" fill="#fff" />
                          <rect x="36" y="2" width="22" height="22" rx="3" fill={COLORS.navy} />
                          <rect x="42" y="8" width="10" height="10" rx="1" fill="#fff" />
                          <rect x="2" y="36" width="22" height="22" rx="3" fill={COLORS.navy} />
                          <rect x="8" y="42" width="10" height="10" rx="1" fill="#fff" />
                          <rect x="36" y="36" width="6" height="6" rx="1" fill={COLORS.navy} />
                          <rect x="46" y="36" width="6" height="6" rx="1" fill={COLORS.navy} />
                          <rect x="36" y="46" width="6" height="6" rx="1" fill={COLORS.navy} />
                          <rect x="46" y="46" width="12" height="6" rx="1" fill={COLORS.navy} />
                          <rect x="28" y="28" width="6" height="6" rx="1" fill={COLORS.navy} />
                          <rect x="28" y="2" width="6" height="6" rx="1" fill={COLORS.navy} />
                          <rect x="28" y="12" width="6" height="4" rx="1" fill={COLORS.navy} />
                          <rect x="2" y="28" width="6" height="6" rx="1" fill={COLORS.navy} />
                          <rect x="12" y="28" width="4" height="6" rx="1" fill={COLORS.navy} />
                        </svg>
                      </div>
                    </div>
                    <p style={{
                      margin: 0, fontSize: 12, color: COLORS.grey,
                      wordBreak: 'break-all', fontFamily: "'Courier New', monospace",
                    }}>
                      {pageUrl}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {!loading && result?.valid === false && (
            <motion.div
              key="invalid"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div style={{
                background: '#fff', borderRadius: 16, overflow: 'hidden',
                boxShadow: '0 4px 24px rgba(239,68,68,0.12)',
                border: `2px solid ${COLORS.error}`,
              }}>
                {/* Red header */}
                <div style={{
                  background: `linear-gradient(135deg, ${COLORS.error}, #DC2626)`,
                  padding: '28px',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  textAlign: 'center', gap: 12,
                }}>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 280, damping: 18, delay: 0.1 }}
                  >
                    <XCircleIcon style={{ width: 60, height: 60, color: '#fff' }} />
                  </motion.div>
                  <div>
                    <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 800, color: '#fff' }}>
                      ID Not Found or Invalid
                    </h2>
                    <code style={{
                      fontSize: 14, background: 'rgba(0,0,0,0.2)', color: '#fff',
                      padding: '2px 10px', borderRadius: 6, fontWeight: 700,
                    }}>
                      {queriedId}
                    </code>
                  </div>
                </div>

                <div style={{ padding: '24px 28px' }}>
                  <div style={{
                    background: '#FEF2F2', border: '1px solid #FCA5A5',
                    borderRadius: 10, padding: '14px 18px',
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                  }}>
                    <ShieldExclamationIcon style={{ width: 20, height: 20, color: COLORS.error, flexShrink: 0, marginTop: 1 }} />
                    <p style={{ margin: 0, fontSize: 14, color: '#991B1B', lineHeight: 1.6 }}>
                      This School ID could not be verified. Please contact the school directly to confirm student enrollment.
                    </p>
                  </div>

                  <div style={{ marginTop: 18 }}>
                    <p style={{ margin: '0 0 12px', fontSize: 13, color: COLORS.grey }}>
                      Possible reasons this ID is invalid:
                    </p>
                    <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {[
                        'The ID number was entered incorrectly',
                        'The student is no longer enrolled',
                        'The ID has expired',
                        'This is a fraudulent or forged ID',
                      ].map((item, i) => (
                        <li key={i} style={{ fontSize: 14, color: COLORS.navy }}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Help note — always visible */}
        {!loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            style={{
              marginTop: 24, textAlign: 'center',
              fontSize: 13, color: COLORS.grey, lineHeight: 1.6,
            }}
          >
            This verification page can be accessed by anyone to check the validity of a SpeakIQ school ID.
            <br />
            If you have questions, contact your school directly.
          </motion.div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        background: COLORS.navy, padding: '18px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 26, height: 26, borderRadius: 6, background: COLORS.teal,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AcademicCapIcon style={{ width: 16, height: 16, color: '#fff' }} />
          </div>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>SpeakIQ</span>
        </div>
        <span style={{ color: COLORS.grey, fontSize: 13 }}>·</span>
        <span style={{ color: COLORS.grey, fontSize: 13 }}>
          Powered by SpeakIQ — English Learning Platform
        </span>
        <span style={{ color: COLORS.grey, fontSize: 13 }}>·</span>
        <span style={{ color: COLORS.grey, fontSize: 13 }}>
          © {new Date().getFullYear()}
        </span>
      </div>
    </div>
  );
}
